import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const LANG_OPTIONS = ['ko', 'en', 'ja', 'zh'];
const TREND_DAYS = 14;
const TIMEZONE = 'Asia/Seoul';
const PAGE_SIZE = 1000;

const pad2 = (n) => String(n).padStart(2, '0');

const ymdToLabel = (ymd) => {
  if (!ymd || ymd.length !== 8) return ymd || '-';
  return `${ymd.slice(4, 6)}/${ymd.slice(6, 8)}`;
};

/** Asia/Seoul 기준 YYYYMMDD */
const toYmdInTz = (date, timeZone = TIMEZONE) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}${get('month')}${get('day')}`;
};

/** YYYYMMDD(KST) → 해당일 00:00:00 KST의 UTC ISO */
const kstYmdStartIso = (ymd) => {
  const y = ymd.slice(0, 4);
  const m = ymd.slice(4, 6);
  const d = ymd.slice(6, 8);
  return new Date(`${y}-${m}-${d}T00:00:00+09:00`).toISOString();
};

/** YYYYMMDD(KST) → 해당일 23:59:59.999 KST의 UTC ISO */
const kstYmdEndIso = (ymd) => {
  const y = ymd.slice(0, 4);
  const m = ymd.slice(4, 6);
  const d = ymd.slice(6, 8);
  return new Date(`${y}-${m}-${d}T23:59:59.999+09:00`).toISOString();
};

const addDaysToYmd = (ymd, days) => {
  const utc = new Date(`${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}T12:00:00+09:00`);
  utc.setDate(utc.getDate() + days);
  return toYmdInTz(utc);
};

const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

const emptyTrend = (endYmd = toYmdInTz(new Date())) => {
  const rows = [];
  for (let i = TREND_DAYS - 1; i >= 0; i -= 1) {
    const ymd = addDaysToYmd(endYmd, -i);
    rows.push({
      key: ymd,
      label: `${ymd.slice(4, 6)}/${ymd.slice(6, 8)}`,
      count: 0,
    });
  }
  return rows;
};

/** created_at을 Asia/Seoul 일자(YYYYMMDD)로 묶어 카운트 */
const countByYmdCreatedAt = (rows, field = 'created_at') => {
  const map = {};
  (rows || []).forEach((row) => {
    const raw = row?.[field];
    if (!raw) return;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return;
    const key = toYmdInTz(d);
    map[key] = (map[key] || 0) + 1;
  });
  return map;
};

/** PostgREST 1000건 제한을 피하기 위해 페이지네이션으로 전체 조회 */
const fetchAllRows = async (buildQuery) => {
  const all = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error) return { data: null, error };
    const chunk = data || [];
    all.push(...chunk);
    if (chunk.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return { data: all, error: null };
};

const SimpleBarChart = ({ title, data, color = '#4e73df', unit = '건' }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="card shadow h-100 mb-0">
      <div className="card-header py-3">
        <h6 className="m-0 font-weight-bold text-primary">{title}</h6>
      </div>
      <div className="card-body">
        <div className="d-flex align-items-end" style={{ gap: 4, height: 140 }}>
          {data.map((item) => (
            <div key={item.key} className="flex-fill d-flex flex-column align-items-center justify-content-end" style={{ minWidth: 0 }}>
              <small className="text-muted mb-1" style={{ fontSize: 10 }}>
                {item.count > 0 ? item.count : ''}
              </small>
              <div
                title={`${item.label}: ${item.count}${unit}`}
                style={{
                  width: '70%',
                  maxWidth: 18,
                  height: `${Math.max(item.count > 0 ? 8 : 2, (item.count / max) * 110)}px`,
                  background: color,
                  borderRadius: '4px 4px 0 0',
                  opacity: item.count > 0 ? 1 : 0.25,
                }}
              />
            </div>
          ))}
        </div>
        <div className="d-flex mt-2" style={{ gap: 4 }}>
          {data.map((item) => (
            <div key={`l-${item.key}`} className="flex-fill text-center text-muted" style={{ fontSize: 9, minWidth: 0 }}>
              {item.label.slice(3)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MonthHeatmap = ({ title, cells, linkBase }) => (
  <div className="card shadow h-100 mb-0">
    <div className="card-header py-3 d-flex justify-content-between align-items-center">
      <h6 className="m-0 font-weight-bold text-primary">{title}</h6>
      {linkBase ? (
        <Link to={linkBase} className="btn btn-sm btn-outline-primary">
          관리
        </Link>
      ) : null}
    </div>
    <div className="card-body">
      <div className="d-flex flex-wrap" style={{ gap: 4 }}>
        {cells.map((cell) => (
          <div
            key={cell.ymd}
            title={`${ymdToLabel(cell.ymd)}: ${cell.ok ? '등록' : '없음'}`}
            style={{
              width: 22,
              height: 22,
              borderRadius: 4,
              background: cell.ok ? '#1cc88a' : '#e3e6f0',
              border: cell.isToday ? '2px solid #4e73df' : '1px solid #d1d3e2',
              fontSize: 9,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: cell.ok ? '#fff' : '#858796',
              fontWeight: 600,
            }}
          >
            {Number(cell.ymd.slice(6, 8))}
          </div>
        ))}
      </div>
      <div className="mt-2 small text-muted">
        <span className="mr-3">
          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#1cc88a', borderRadius: 2, marginRight: 4 }} />
          등록
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#e3e6f0', borderRadius: 2, marginRight: 4 }} />
          미등록
        </span>
      </div>
    </div>
  </div>
);

const KpiCard = ({ title, value, subtitle, color = 'primary', to }) => {
  const body = (
    <div className={`card border-left-${color} shadow h-100 py-2 mb-0`}>
      <div className="card-body">
        <div className="row no-gutters align-items-center">
          <div className="col mr-2">
            <div className={`text-xs font-weight-bold text-${color} text-uppercase mb-1`}>{title}</div>
            <div className="h5 mb-0 font-weight-bold text-gray-800">{value ?? '—'}</div>
            {subtitle ? <div className="small text-muted mt-1">{subtitle}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
  return to ? (
    <Link to={to} className="text-decoration-none">
      {body}
    </Link>
  ) : (
    body
  );
};

const StatusPill = ({ ok, label, to }) => {
  const className = ok ? 'badge badge-success' : 'badge badge-danger';
  const text = ok ? `${label} ✓` : `${label} ✗`;
  if (to) {
    return (
      <Link to={to} className={`${className} p-2 mr-2 mb-2 d-inline-block`} style={{ fontSize: 13 }}>
        {text}
      </Link>
    );
  }
  return <span className={`${className} p-2 mr-2 mb-2 d-inline-block`} style={{ fontSize: 13 }}>{text}</span>;
};

const Dashboard = () => {
  const [language, setLanguage] = useState('ko');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const [signupTrend, setSignupTrend] = useState(emptyTrend);
  const [meditationTrend, setMeditationTrend] = useState(emptyTrend);
  const [quizTrend, setQuizTrend] = useState(emptyTrend);

  const [qtHeatmap, setQtHeatmap] = useState([]);
  const [quizHeatmap, setQuizHeatmap] = useState([]);
  const [familyHeatmap, setFamilyHeatmap] = useState([]);
  const [bibleStudyHeatmap, setBibleStudyHeatmap] = useState([]);
  const [audioHeatmap, setAudioHeatmap] = useState([]);
  const [videoHeatmap, setVideoHeatmap] = useState([]);

  const [kpi, setKpi] = useState({
    totalUsers: null,
    todaySignups: null,
    activeSubs: null,
    pendingInquiries: null,
    pendingSubscribe: null,
  });

  const [todayContent, setTodayContent] = useState({
    qt: false,
    quiz: false,
    bibleStudy: false,
    family: false,
    video: false,
    audio: false,
  });

  const [todayEngage, setTodayEngage] = useState({
    meditations: null,
    highlights: null,
    gratitude: null,
    quizSessions: null,
    bibleStudyAnswers: null,
  });

  const [alerts, setAlerts] = useState({
    inquiries: [],
    expiring: [],
    monthGaps: { qt: [], quiz: [], family: [], bibleStudy: [], audio: [], video: [] },
    aiUsageToday: null,
  });

  const today = useMemo(() => new Date(), []);
  const todayYmd = useMemo(() => toYmdInTz(today), [today]);
  const todayIso = useMemo(() => {
    const ymd = toYmdInTz(today);
    return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
  }, [today]);
  const monthLabel = useMemo(() => {
    const ymd = toYmdInTz(today);
    return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}`;
  }, [today]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    const warns = [];

    const kstTodayYmd = toYmdInTz(today);
    const trendStartYmd = addDaysToYmd(kstTodayYmd, -(TREND_DAYS - 1));
    const trendStartIso = kstYmdStartIso(trendStartYmd);
    const todayEndIso = kstYmdEndIso(kstTodayYmd);
    const todayStartIso = kstYmdStartIso(kstTodayYmd);

    const year = Number(kstTodayYmd.slice(0, 4));
    const month = Number(kstTodayYmd.slice(4, 6)) - 1;
    const lastDay = daysInMonth(year, month);
    const monthStartYmd = `${year}${pad2(month + 1)}01`;
    const monthEndYmd = `${year}${pad2(month + 1)}${pad2(lastDay)}`;

    const expireEndYmd = addDaysToYmd(kstTodayYmd, 7);

    const settle = async (label, promise) => {
      try {
        const result = await promise;
        if (result?.error) {
          warns.push(`${label}: ${result.error.message}`);
          return { data: null, count: null, error: result.error };
        }
        return result;
      } catch (e) {
        warns.push(`${label}: ${e.message || e}`);
        return { data: null, count: null, error: e };
      }
    };

    try {
      const [
        usersTotal,
        usersToday,
        usersTrend,
        meditationsTrend,
        quizActivityTrend,
        qtMonth,
        quizMonth,
        familyMonth,
        bibleStudyMonth,
        audioMonth,
        videoMonth,
        qtToday,
        quizToday,
        bibleToday,
        familyToday,
        videoToday,
        audioToday,
        medToday,
        hlToday,
        gratToday,
        quizSessToday,
        bibleAnsToday,
        inquiriesPending,
        subscribePending,
        activeSubsCount,
        recentInquiries,
        expiringUsers,
        aiToday,
      ] = await Promise.all([
        settle('전체 사용자', supabase.from('users').select('id', { count: 'exact', head: true })),
        settle(
          '오늘 가입',
          supabase
            .from('users')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', todayStartIso)
            .lte('created_at', todayEndIso)
        ),
        settle(
          '가입 추이',
          fetchAllRows(() =>
            supabase
              .from('users')
              .select('created_at')
              .gte('created_at', trendStartIso)
              .lte('created_at', todayEndIso)
              .order('created_at', { ascending: true })
          )
        ),
        settle(
          '묵상 추이',
          fetchAllRows(() =>
            supabase
              .from('user_meditations')
              .select('created_at')
              .gte('created_at', trendStartIso)
              .lte('created_at', todayEndIso)
              .order('created_at', { ascending: true })
          )
        ),
        settle(
          '퀴즈 추이',
          fetchAllRows(() =>
            supabase
              .from('user_activities')
              .select('created_at')
              .eq('activity_type', 'quiz_session_completed')
              .gte('created_at', trendStartIso)
              .lte('created_at', todayEndIso)
              .order('created_at', { ascending: true })
          )
        ),
        settle(
          '이번 달 QT',
          supabase
            .from('daily_scriptures')
            .select('qt_ymd, is_active')
            .eq('language', language)
            .gte('qt_ymd', monthStartYmd)
            .lte('qt_ymd', monthEndYmd)
        ),
        settle(
          '이번 달 퀴즈',
          supabase
            .from('quiz_sets')
            .select('qt_ymd, quiz_date, is_active')
            .eq('language', language)
            .gte('qt_ymd', monthStartYmd)
            .lte('qt_ymd', monthEndYmd)
        ),
        settle(
          '이번 달 가정예배',
          supabase
            .from('family_devotions')
            .select('devotions_date, language, is_active')
            .eq('language', language)
            .gte('devotions_date', monthStartYmd)
            .lte('devotions_date', monthEndYmd)
        ),
        settle(
          '이번 달 성경공부',
          supabase
            .from('bible_study')
            .select('study_date, is_active')
            .eq('language', language)
            .gte('study_date', monthStartYmd)
            .lte('study_date', monthEndYmd)
        ),
        settle(
          '이번 달 음성',
          supabase
            .from('daily_scriptures')
            .select('qt_ymd, qt_audio_url, is_active')
            .eq('language', language)
            .gte('qt_ymd', monthStartYmd)
            .lte('qt_ymd', monthEndYmd)
            .not('qt_audio_url', 'is', null)
            .neq('qt_audio_url', '')
        ),
        settle(
          '이번 달 영상',
          supabase
            .from('qt_video_youtube')
            .select('qt_ymd')
            .gte('qt_ymd', monthStartYmd)
            .lte('qt_ymd', monthEndYmd)
        ),
        settle(
          '오늘 QT',
          supabase
            .from('daily_scriptures')
            .select('id, qt_audio_url, is_active')
            .eq('language', language)
            .eq('qt_ymd', todayYmd)
            .limit(5)
        ),
        settle(
          '오늘 퀴즈',
          supabase
            .from('quiz_sets')
            .select('id, is_active')
            .eq('language', language)
            .or(`qt_ymd.eq.${todayYmd},quiz_date.eq.${todayIso}`)
            .limit(5)
        ),
        settle(
          '오늘 성경공부',
          supabase
            .from('bible_study')
            .select('id, is_active')
            .eq('language', language)
            .eq('study_date', todayYmd)
            .limit(5)
        ),
        settle(
          '오늘 가정예배',
          supabase
            .from('family_devotions')
            .select('id, is_active')
            .eq('language', language)
            .eq('devotions_date', todayYmd)
            .limit(5)
        ),
        settle(
          '오늘 영상',
          supabase.from('qt_video_youtube').select('id').eq('qt_ymd', todayYmd).limit(5)
        ),
        settle(
          '오늘 음성(QT)',
          supabase
            .from('daily_scriptures')
            .select('id, qt_audio_url')
            .eq('language', language)
            .eq('qt_ymd', todayYmd)
            .not('qt_audio_url', 'is', null)
            .neq('qt_audio_url', '')
            .limit(5)
        ),
        settle(
          '오늘 묵상',
          supabase
            .from('user_meditations')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', todayStartIso)
            .lte('created_at', todayEndIso)
        ),
        settle(
          '오늘 하이라이트',
          supabase
            .from('highlighted_verses')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', todayStartIso)
            .lte('created_at', todayEndIso)
        ),
        settle(
          '오늘 감사일기',
          supabase
            .from('gratitude_journal')
            .select('id', { count: 'exact', head: true })
            .eq('wt_ymd', todayYmd)
        ),
        settle(
          '오늘 퀴즈완료',
          supabase
            .from('user_activities')
            .select('id', { count: 'exact', head: true })
            .eq('activity_type', 'quiz_session_completed')
            .gte('created_at', todayStartIso)
            .lte('created_at', todayEndIso)
        ),
        settle(
          '오늘 성경공부 답변',
          supabase
            .from('bible_study_a')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', todayStartIso)
            .lte('created_at', todayEndIso)
        ),
        settle(
          '미처리 문의',
          supabase
            .from('inquiries')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'processing')
        ),
        settle(
          '구독확인 대기',
          supabase
            .from('subscribe_confirm')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'PENDING')
        ),
        settle(
          '활성 구독',
          supabase
            .from('user_subscriptions')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'active')
            .gt('expires_at', new Date().toISOString())
        ),
        settle(
          '최근 문의',
          supabase
            .from('inquiries')
            .select('id, title, status, created_at')
            .eq('status', 'processing')
            .order('created_at', { ascending: false })
            .limit(5)
        ),
        settle(
          '만료 임박',
          supabase
            .from('user_info')
            .select('user_info_id, nickname, gd_end_ymd, user_id')
            .gte('gd_end_ymd', todayYmd)
            .lte('gd_end_ymd', expireEndYmd)
            .order('gd_end_ymd', { ascending: true })
            .limit(10)
        ),
        settle(
          'AI 사용량',
          supabase
            .from('ai_api_usage')
            .select('usage_count')
            .eq('usage_ymd', todayYmd)
        ),
      ]);

      const signupMap = countByYmdCreatedAt(usersTrend.data);
      const meditationMap = countByYmdCreatedAt(meditationsTrend.data);
      const quizMap = countByYmdCreatedAt(quizActivityTrend.data);
      const baseTrend = emptyTrend(kstTodayYmd);
      setSignupTrend(baseTrend.map((r) => ({ ...r, count: signupMap[r.key] || 0 })));
      setMeditationTrend(baseTrend.map((r) => ({ ...r, count: meditationMap[r.key] || 0 })));
      setQuizTrend(baseTrend.map((r) => ({ ...r, count: quizMap[r.key] || 0 })));

      const buildHeat = (ymdSet) => {
        const cells = [];
        for (let day = 1; day <= lastDay; day += 1) {
          const ymd = `${year}${pad2(month + 1)}${pad2(day)}`;
          cells.push({
            ymd,
            ok: ymdSet.has(ymd),
            isToday: ymd === todayYmd,
          });
        }
        return cells;
      };

      const qtSet = new Set(
        (qtMonth.data || [])
          .filter((r) => r.is_active !== 'N')
          .map((r) => r.qt_ymd)
          .filter(Boolean)
      );
      const quizSet = new Set(
        (quizMonth.data || [])
          .filter((r) => r.is_active !== 'N')
          .map((r) => {
            if (r.qt_ymd) return r.qt_ymd;
            if (r.quiz_date) return String(r.quiz_date).replace(/-/g, '').slice(0, 8);
            return null;
          })
          .filter(Boolean)
      );
      const familySet = new Set(
        (familyMonth.data || [])
          .filter((r) => r.is_active !== 'N')
          .map((r) => r.devotions_date)
          .filter(Boolean)
      );
      const bibleStudySet = new Set(
        (bibleStudyMonth.data || [])
          .filter((r) => r.is_active !== 'N')
          .map((r) => {
            const raw = r.study_date;
            if (!raw) return null;
            return String(raw).replace(/-/g, '').slice(0, 8);
          })
          .filter(Boolean)
      );
      const audioSet = new Set(
        (audioMonth.data || [])
          .filter((r) => r.is_active !== 'N')
          .filter((r) => r.qt_audio_url && String(r.qt_audio_url).trim())
          .map((r) => r.qt_ymd)
          .filter(Boolean)
      );
      const videoSet = new Set(
        (videoMonth.data || [])
          .map((r) => r.qt_ymd)
          .filter(Boolean)
      );

      const qtCells = buildHeat(qtSet);
      const quizCells = buildHeat(quizSet);
      const familyCells = buildHeat(familySet);
      const bibleStudyCells = buildHeat(bibleStudySet);
      const audioCells = buildHeat(audioSet);
      const videoCells = buildHeat(videoSet);
      setQtHeatmap(qtCells);
      setQuizHeatmap(quizCells);
      setFamilyHeatmap(familyCells);
      setBibleStudyHeatmap(bibleStudyCells);
      setAudioHeatmap(audioCells);
      setVideoHeatmap(videoCells);

      const qtRows = qtToday.data || [];
      const hasQt = qtRows.some((r) => r.is_active !== 'N');
      const hasAudio =
        (audioToday.data || []).length > 0 ||
        qtRows.some((r) => r.qt_audio_url && String(r.qt_audio_url).trim());

      setTodayContent({
        qt: hasQt,
        quiz: (quizToday.data || []).some((r) => r.is_active !== 'N'),
        bibleStudy: (bibleToday.data || []).some((r) => r.is_active !== 'N'),
        family: (familyToday.data || []).some((r) => r.is_active !== 'N'),
        video: (videoToday.data || []).length > 0,
        audio: hasAudio,
      });

      setKpi({
        totalUsers: usersTotal.count,
        todaySignups: usersToday.count,
        activeSubs: activeSubsCount.count,
        pendingInquiries: inquiriesPending.count,
        pendingSubscribe: subscribePending.count,
      });

      setTodayEngage({
        meditations: medToday.count,
        highlights: hlToday.count,
        gratitude: gratToday.count,
        quizSessions: quizSessToday.count,
        bibleStudyAnswers: bibleAnsToday.count,
      });

      const gapFrom = (cells) =>
        cells.filter((c) => !c.ok && c.ymd <= todayYmd).map((c) => c.ymd).slice(-10);

      const aiSum = (aiToday.data || []).reduce((sum, row) => sum + (Number(row.usage_count) || 0), 0);

      setAlerts({
        inquiries: recentInquiries.data || [],
        expiring: expiringUsers.data || [],
        monthGaps: {
          qt: gapFrom(qtCells),
          quiz: gapFrom(quizCells),
          family: gapFrom(familyCells),
          bibleStudy: gapFrom(bibleStudyCells),
          audio: gapFrom(audioCells),
          video: gapFrom(videoCells),
        },
        aiUsageToday: aiToday.error ? null : aiSum,
      });

      setWarnings(warns);
    } catch (e) {
      console.error(e);
      setError(e.message || '대시보드를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [language, today, todayIso, todayYmd]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const formatCount = (n) => (n == null ? '—' : Number(n).toLocaleString());

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-1 text-gray-800">Dashboard</h1>
          <div className="text-muted small">
            {todayIso} · 콘텐츠 언어{' '}
            <select
              className="form-control form-control-sm d-inline-block"
              style={{ width: 80 }}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANG_OPTIONS.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button type="button" className="btn btn-sm btn-primary shadow-sm" onClick={loadDashboard} disabled={loading}>
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {warnings.length > 0 && (
        <div className="alert alert-warning small">
          일부 지표를 불러오지 못했습니다 (권한/RLS 가능). {warnings.slice(0, 3).join(' · ')}
          {warnings.length > 3 ? ` 외 ${warnings.length - 3}건` : ''}
        </div>
      )}

      {/* ========== 2순위: 차트 (맨 위) ========== */}
      <div className="mb-2">
        <h6 className="font-weight-bold text-uppercase text-muted">트렌드 · 이번 달 등록 현황</h6>
      </div>
      <div className="row">
        <div className="col-xl-4 col-lg-6 mb-4">
          <SimpleBarChart title={`최근 ${TREND_DAYS}일 가입`} data={signupTrend} color="#4e73df" unit="명" />
        </div>
        <div className="col-xl-4 col-lg-6 mb-4">
          <SimpleBarChart title={`최근 ${TREND_DAYS}일 묵상 작성`} data={meditationTrend} color="#1cc88a" />
        </div>
        <div className="col-xl-4 col-lg-6 mb-4">
          <SimpleBarChart title={`최근 ${TREND_DAYS}일 퀴즈 완료`} data={quizTrend} color="#f6c23e" />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-lg-4 mb-4 mb-lg-0">
          <MonthHeatmap title={`${monthLabel} QT 본문 (${language})`} cells={qtHeatmap} linkBase="/qt/DailyScriptureList" />
        </div>
        <div className="col-lg-4 mb-4 mb-lg-0">
          <MonthHeatmap title={`${monthLabel} 퀴즈 (${language})`} cells={quizHeatmap} linkBase="/quiz" />
        </div>
        <div className="col-lg-4 mb-4">
          <MonthHeatmap title={`${monthLabel} 가정예배 (${language})`} cells={familyHeatmap} linkBase="/family-worship" />
        </div>
        <div className="col-lg-4 mb-4 mb-lg-0">
          <MonthHeatmap title={`${monthLabel} 성경공부 (${language})`} cells={bibleStudyHeatmap} linkBase="/bible-study" />
        </div>
        <div className="col-lg-4 mb-4 mb-lg-0">
          <MonthHeatmap title={`${monthLabel} 음성 (${language})`} cells={audioHeatmap} linkBase="/qt/audios" />
        </div>
        <div className="col-lg-4 mb-4 mb-lg-0">
          <MonthHeatmap title={`${monthLabel} 영상`} cells={videoHeatmap} linkBase="/qt/videos" />
        </div>
      </div>

      {/* ========== 1순위: KPI + 오늘 콘텐츠 + 오늘 참여 ========== */}
      <div className="mb-2">
        <h6 className="font-weight-bold text-uppercase text-muted">오늘 운영 요약</h6>
      </div>
      <div className="row">
        <div className="col-xl-2 col-md-4 mb-4">
          <KpiCard title="전체 사용자" value={formatCount(kpi.totalUsers)} color="primary" to="/users" />
        </div>
        <div className="col-xl-2 col-md-4 mb-4">
          <KpiCard title="오늘 가입" value={formatCount(kpi.todaySignups)} color="success" to="/users" />
        </div>
        <div className="col-xl-2 col-md-4 mb-4">
          <KpiCard title="활성 구독" value={formatCount(kpi.activeSubs)} color="info" subtitle="" />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard title="미처리 문의" value={formatCount(kpi.pendingInquiries)} color="warning" to="/inquiries?status=processing" />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <KpiCard title="구독확인 대기" value={formatCount(kpi.pendingSubscribe)} color="danger" subtitle="" />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">오늘 콘텐츠 체크 ({language})</h6>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-muted">조회 중…</div>
              ) : (
                <div>
                  <StatusPill ok={todayContent.qt} label="QT 본문" to="/qt/DailyScriptureList" />
                  <StatusPill ok={todayContent.quiz} label="퀴즈" to="/quiz" />
                  <StatusPill ok={todayContent.bibleStudy} label="성경공부" to="/bible-study" />
                  <StatusPill ok={todayContent.family} label="가정예배" to="/family-worship" />
                  <StatusPill ok={todayContent.video} label="영상" to="/qt/videos" />
                  <StatusPill ok={todayContent.audio} label="음성" to="/qt/audios" />
                  <p className="small text-muted mb-0 mt-3">
                    빨간 ✗ 항목을 눌러 해당 관리 화면으로 이동하세요. 음성은 오늘 QT 기준입니다.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-6 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">오늘 참여</h6>
            </div>
            <div className="card-body">
              <div className="row">
                {[
                  { label: '묵상 작성', value: todayEngage.meditations },
                  { label: '하이라이트', value: todayEngage.highlights },
                  { label: '감사일기', value: todayEngage.gratitude },
                  { label: '퀴즈 완료', value: todayEngage.quizSessions },
                  { label: '성경공부 답변', value: todayEngage.bibleStudyAnswers },
                ].map((item) => (
                  <div key={item.label} className="col-6 col-md-4 mb-3">
                    <div className="text-xs text-uppercase text-muted font-weight-bold">{item.label}</div>
                    <div className="h4 mb-0 font-weight-bold text-gray-800">{formatCount(item.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== 3순위: 알림 (맨 아래) ========== */}
      <div className="mb-2">
        <h6 className="font-weight-bold text-uppercase text-muted">운영 알림</h6>
      </div>
      <div className="row">
        <div className="col-lg-4 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3 d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold text-primary">미처리 문의</h6>
              <Link to="/inquiries?status=processing" className="btn btn-sm btn-outline-primary">
                전체보기
              </Link>
            </div>
            <div className="card-body">
              {alerts.inquiries.length === 0 ? (
                <div className="text-muted small">대기 중인 문의가 없습니다.</div>
              ) : (
                <ul className="list-group list-group-flush">
                  {alerts.inquiries.map((item) => (
                    <li key={item.id} className="list-group-item px-0">
                      <Link
                        to={`/inquiries/${item.id}`}
                        className="d-flex justify-content-between text-decoration-none text-gray-800"
                      >
                        <span className="text-truncate mr-2" title={item.title}>
                          {item.title}
                        </span>
                        <small className="text-muted text-nowrap">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                        </small>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 small text-muted">
                상태: 처리중 · 총 {formatCount(kpi.pendingInquiries)}건
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">구독 만료 임박 (7일)</h6>
            </div>
            <div className="card-body">
              {alerts.expiring.length === 0 ? (
                <div className="text-muted small">7일 이내 만료 예정 사용자가 없습니다.</div>
              ) : (
                <ul className="list-group list-group-flush">
                  {alerts.expiring.map((item) => (
                    <li key={`${item.user_id}-${item.gd_end_ymd}`} className="list-group-item px-0 d-flex justify-content-between">
                      <span className="text-truncate mr-2">
                        {item.nickname || item.user_info_id || item.user_id?.slice?.(0, 8)}
                      </span>
                      <small className="text-danger text-nowrap">{item.gd_end_ymd}</small>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 small text-muted"> 구독확인 대기 {formatCount(kpi.pendingSubscribe)}건</div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">AI 사용</h6>
            </div>
            <div className="card-body">
              {/* <div className="mb-3">
                <div className="small font-weight-bold mb-1">QT 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.qt.length
                    ? alerts.monthGaps.qt.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div>
              <div className="mb-3">
                <div className="small font-weight-bold mb-1">퀴즈 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.quiz.length
                    ? alerts.monthGaps.quiz.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div>
              <div className="mb-3">
                <div className="small font-weight-bold mb-1">가정예배 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.family.length
                    ? alerts.monthGaps.family.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div>
              <div className="mb-3">
                <div className="small font-weight-bold mb-1">성경공부 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.bibleStudy?.length
                    ? alerts.monthGaps.bibleStudy.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div>
              <div className="mb-3">
                <div className="small font-weight-bold mb-1">음성 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.audio?.length
                    ? alerts.monthGaps.audio.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div> */}
              {/* <div className="mb-3">
                <div className="small font-weight-bold mb-1">영상 미등록 (오늘까지)</div>
                <div className="small text-muted">
                  {alerts.monthGaps.video?.length
                    ? alerts.monthGaps.video.map(ymdToLabel).join(', ')
                    : '없음'}
                </div>
              </div> */}
              {/* <hr /> */}
              <div>
                <div className="small font-weight-bold mb-1">오늘 AI API 사용 합계 ({todayYmd})</div>
                <div className="h5 mb-0">{formatCount(alerts.aiUsageToday)} 건</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
